/**
 * shared/notification-types.ts
 *
 * The wire contract for the in-app notification feed (`GET /api/notifications`).
 *
 * WHY THIS EXISTS: the notification bell was previously wired to ONE source —
 * `/api/dashboard/:orgId/alerts` (OSINT anomaly events). Every other thing that
 * actually needs a person's attention (a challenge awaiting their response, a
 * bank account waiting on quorum, a policy change awaiting consent, an
 * administrator grant awaiting their co-signature, a pending invite or
 * membership request) reached no notification surface at all, so an org with
 * four open approvals still rendered "No active alerts."
 *
 * HONEST SCOPE (CLAUDE.md §Friction Rule clause 2): a notification is a NOTICE
 * about an already-committed, already-attributed state — it grants no
 * capability, changes no AAL, quorum, or step-up floor, and carries no
 * liveness/deepfake claim. Acting on any item still runs that item's own
 * ceremony with its own gates, unchanged.
 *
 * DATA MINIMISATION (Rule 10): items carry ids, coarse counts, display names
 * the viewer can already see on the linked surface, and enum-ish kinds. No
 * account digits, no ciphertext, no cert bytes, no PII beyond a display name
 * the same viewer already reads on the destination page.
 */

/**
 * Visual + ordering weight. `critical` is reserved for the Forensic-Pause /
 * OSINT-critical class (CLAUDE.md §Design Constraints reserve red for exactly
 * that); `attention` is the ordinary "you need to act on this" amber class;
 * `info` is neutral.
 */
export type NotificationSeverity = "critical" | "attention" | "info";

/**
 * The source that produced an item. Each value maps 1:1 to one source query in
 * `server/logic/notifications/sources/`, so an unexplained item is always
 * traceable to exactly one reader.
 */
export type NotificationKind =
  | "anomaly_alert"
  | "challenge_awaiting_response"
  | "bank_account_quorum"
  | "policy_change_pending"
  | "admin_grant_cosign"
  | "member_invite_pending"
  /**
   * An outbound COUNTERPARTY connection invite (`org`/`individual` kind).
   * Distinct from `member_invite_pending` because it lives on /connections —
   * /dashboard/members filters these out, so sharing one kind (and therefore
   * one href) would dead-end the user on a page that provably cannot show it.
   */
  | "connection_invite_pending"
  | "membership_request_pending"
  | "forensic_recovery_notice"
  /**
   * A colleague nudged the caller about a pending approval they are
   * genuinely awaited on (Internal Team Conversations spec module 05,
   * AC-19/AC-20). Live-filtered at read time — an item for an
   * already-resolved approval vanishes on its own (see
   * `sources-approval-nudge.ts`), never a persisted "resolved" flag.
   */
  | "approval_nudge"
  /**
   * A passkey was appended to the caller's own account, or to an account they
   * guard (Stage 7 "passkey boundary gaps" module 01, AC-1). Durable half of
   * the `security:credential_added` / `guardian:credential_added` WS fan-out
   * (`credential-append-notice.ts`) — the pull-based route a closed app comes
   * back to. Never actionable: nothing is granted or withheld by reading it.
   */
  | "credential_append_notice";

/**
 * Runtime mirror of `NotificationKind` (spec `2026-09-04-universal-coverage`
 * module 04 §2, AC-12 — Task 21's "smallest runtime mirror... with a
 * type-level equality check"): a TS union has no runtime form, so
 * `entry-point-coverage.test.ts` cannot enumerate it directly. The
 * `Record<NotificationKind, true>` below is the type-level equality check —
 * missing a union member, or including one that is not (or no longer) a
 * member, both fail `pnpm exec tsc --noEmit` on THIS file, so the exported array
 * cannot silently drift from the type it mirrors.
 */
const NOTIFICATION_KIND_KEYS: Record<NotificationKind, true> = {
  anomaly_alert: true,
  challenge_awaiting_response: true,
  bank_account_quorum: true,
  policy_change_pending: true,
  admin_grant_cosign: true,
  member_invite_pending: true,
  connection_invite_pending: true,
  membership_request_pending: true,
  forensic_recovery_notice: true,
  approval_nudge: true,
  credential_append_notice: true,
};

export const NOTIFICATION_KINDS: readonly NotificationKind[] = Object.keys(
  NOTIFICATION_KIND_KEYS,
) as NotificationKind[];

export interface NotificationItem {
  /**
   * Stable, source-prefixed id (e.g. `challenge:<uuid>`). Prefixing keeps two
   * sources that happen to share a row id from colliding in a React key or in
   * the dedupe below.
   */
  readonly id: string;
  readonly kind: NotificationKind;
  readonly severity: NotificationSeverity;
  /** Short headline, sentence case, no trailing period. */
  readonly title: string;
  /** One line of context. May be empty. */
  readonly detail: string;
  /** ISO-8601 UTC (Rule 13 — stored UTC, formatted locally by the client). */
  readonly timestamp: string;
  /**
   * ISO-8601 UTC deadline this item is counting down to, for the kinds that
   * have one (today: the forensic-recovery veto window's `hold_until`).
   *
   * STRUCTURED ON PURPOSE. Rule 13 bans a date literal in user-facing copy, so
   * `detail` may never interpolate it; the client formats this field locally
   * instead. Absent whenever there is nothing left to beat — a completed
   * recovery carries no deadline.
   */
  readonly deadline?: string;
  /** In-app destination for this item (wouter path). */
  readonly href: string;
  /**
   * True when the item is only dismissible through the anomaly-alert dismiss
   * route. Every other kind clears by resolving the underlying state, so the
   * drawer must not offer a dismiss control that would silently do nothing.
   */
  readonly dismissible: boolean;
}

/**
 * The wire contract, shared by the server aggregator and the client hook so the
 * two cannot drift. Both sides extend this rather than redeclaring it.
 */
export interface NotificationFeed {
  /** The capped page of items (see NOTIFICATION_FEED_LIMIT). */
  readonly items: readonly NotificationItem[];
  /** Every item the caller has, including `info` — counted BEFORE the cap. */
  readonly total: number;
  /**
   * Items the user can actually DO something about (`critical` + `attention`) —
   * the bell badge count.
   *
   * Split from `total` deliberately. `info` items (an unaccepted invite, an
   * unanswered membership request) are waiting on somebody ELSE, and a
   * counterparty invite lives for up to 7 days — badging them would light a
   * permanent amber dot with no action available to clear it, which trains
   * people to ignore the badge. They still LIST in the drawer; they just do not
   * demand attention.
   */
  readonly actionable: number;
  /** True when at least one source failed and its items are missing. */
  readonly degraded: boolean;
  /** Names of the failed sources, for an honest client-side notice. */
  readonly degradedSources: readonly string[];
}

/** Hard cap on items returned in one feed read. */
export const NOTIFICATION_FEED_LIMIT = 50;

/** Items rendered inside the bell drawer before "View all". */
export const NOTIFICATION_DRAWER_LIMIT = 5;

/**
 * Severities that count toward the bell badge. `info` is excluded — see the
 * `actionable` field above.
 */
export const ACTIONABLE_SEVERITIES: readonly NotificationSeverity[] = [
  "critical",
  "attention",
];

export function isActionable(item: NotificationItem): boolean {
  return ACTIONABLE_SEVERITIES.includes(item.severity);
}

/** Badge count: items the user can act on now. */
export function countActionable(items: readonly NotificationItem[]): number {
  return items.filter(isActionable).length;
}

const SEVERITY_RANK: Record<NotificationSeverity, number> = {
  critical: 0,
  attention: 1,
  info: 2,
};

/**
 * Canonical ordering: severity first (a critical item must never be pushed off
 * the 5-row drawer by a newer routine one), then newest-first within a
 * severity, then id for a stable tie-break.
 */
export function compareNotifications(a: NotificationItem, b: NotificationItem): number {
  const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
  if (bySeverity !== 0) return bySeverity;
  const byTime = new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  if (byTime !== 0) return byTime;
  return a.id.localeCompare(b.id);
}
